import { ConversationDraftSendOptionSection } from '@provision/conversation-types';
import dayjs from 'dayjs';
import type { SendOptionItem } from 'src/pages/Conversations/Chat/InputField/send-options-button';
import {
  ensureFutureDate,
  getDateFromNaturalLanguage,
  resolveNaturalLanguageDate,
} from 'src/utils/natural-language-date';

export interface SendAndCommand {
  id: string;
  label: string;
  description: string;
}

const NATURAL_LANGUAGE_DAY_START = '9:00am';

const SNOOZE_UNIT_MAP: Record<string, dayjs.ManipulateType> = {
  minute: 'minute',
  minutes: 'minute',
  min: 'minute',
  mins: 'minute',
  m: 'minute',
  hour: 'hour',
  hours: 'hour',
  hr: 'hour',
  hrs: 'hour',
  h: 'hour',
  day: 'day',
  days: 'day',
  week: 'week',
  weeks: 'week',
  month: 'month',
  months: 'month',
};

const SNOOZE_KEYWORD_PRESETS: {
  keywords: string[];
  amount: number;
  unit: dayjs.ManipulateType;
}[] = [
  { keywords: ['tomorrow', 'tomorrow morning'], amount: 1, unit: 'day' },
  { keywords: ['next day'], amount: 1, unit: 'day' },
  { keywords: ['next week'], amount: 1, unit: 'week' },
  { keywords: ['next month'], amount: 1, unit: 'month' },
];

// Only these show while nothing that asks for a send action is typed.
const DEFAULT_SEND_AND_IDS = new Set(['send-and-solve', 'send-and-snooze-tomorrow']);

type SnoozeCommandDefinition =
  | { type: 'offset'; amount: number; unit: dayjs.ManipulateType }
  | { type: 'absolute'; iso: string };

const formatSnoozeDuration = (amount: number, unit: dayjs.ManipulateType): string => {
  const unitLabel = amount === 1 ? unit : `${unit}s`;
  return `${amount} ${unitLabel}`;
};

const resolveFutureIso = (input: string): string | null => {
  const parsedWithDayStart = getDateFromNaturalLanguage(input, {
    dayStart: NATURAL_LANGUAGE_DAY_START,
  });
  if (!parsedWithDayStart) {
    return null;
  }
  const parsedWithoutDayStart = getDateFromNaturalLanguage(input);
  const resolved = resolveNaturalLanguageDate(
    parsedWithDayStart,
    parsedWithoutDayStart || parsedWithDayStart,
  );
  const ensured = ensureFutureDate(resolved, input);
  if (!ensured) {
    return null;
  }
  const parsedDate = dayjs(ensured);
  return parsedDate.isValid() ? parsedDate.toISOString() : null;
};

const parseSnoozeCommand = (value: string): SnoozeCommandDefinition | null => {
  const normalized = value.trim().toLowerCase();
  if (!normalized?.includes('snooze')) {
    return null;
  }

  const amountMatch =
    /snooze(?:\s+(?:for|in|after))?\s+(?<amount>a|an|\d+)\s+(?<unit>minutes?|mins?|min|hours?|hrs?|hr|days?|weeks?|months?)/.exec(
      normalized,
    );
  if (amountMatch?.groups) {
    const amountRaw = amountMatch.groups.amount;
    const resolvedAmount =
      amountRaw === 'a' || amountRaw === 'an' ? 1 : Number.parseInt(amountRaw, 10);
    if (Number.isFinite(resolvedAmount) && resolvedAmount > 0) {
      const unitKey = amountMatch.groups.unit;
      const resolvedUnit = SNOOZE_UNIT_MAP[unitKey];
      if (resolvedUnit) {
        return { type: 'offset', amount: resolvedAmount, unit: resolvedUnit };
      }
    }
  }

  for (const preset of SNOOZE_KEYWORD_PRESETS) {
    const hasKeyword = preset.keywords.some(
      (keyword) =>
        normalized.includes(`snooze ${keyword}`) ||
        normalized.includes(`snooze until ${keyword}`) ||
        normalized.includes(`snooze till ${keyword}`),
    );
    if (hasKeyword) {
      return { type: 'offset', amount: preset.amount, unit: preset.unit };
    }
  }

  const snoozeIndex = normalized.indexOf('snooze');
  const remainder = value.slice(snoozeIndex + 'snooze'.length).trim();
  if (remainder) {
    const iso = resolveFutureIso(remainder);
    if (iso) {
      return { type: 'absolute', iso };
    }
  }

  return null;
};

const buildSnoozeCommand = (
  command: SnoozeCommandDefinition,
  displayTime: (value: dayjs.Dayjs | string | Date) => string,
): SendAndCommand => {
  if (command.type === 'offset') {
    const target = dayjs()
      .add(command.amount, command.unit)
      .set('hour', 9)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);

    return {
      id: `send-and-snooze:${command.amount}:${command.unit}`,
      label: `Send and snooze for ${formatSnoozeDuration(command.amount, command.unit)}`,
      description: `Snooze until ${target.format('ddd, MMM D')} at ${displayTime(target)}`,
    };
  }

  const target = dayjs(command.iso);
  const datePart = target.format('ddd, MMM D');
  return {
    id: `send-and-snooze-until:${encodeURIComponent(command.iso)}`,
    label: `Send and snooze until ${datePart}`,
    description: `Snooze until ${datePart} at ${displayTime(target)}`,
  };
};

interface BuildSendAndCommandsInput {
  sendOptions: SendOptionItem[];
  inputValue: string;
  isRescheduling: boolean;
  displayTime: (value: dayjs.Dayjs | string | Date) => string;
}

/** The "Send and …" rows of the time step: send now and then solve or snooze. */
export const buildSendAndCommands = ({
  sendOptions,
  inputValue,
  isRescheduling,
  displayTime,
}: BuildSendAndCommandsInput): SendAndCommand[] => {
  if (isRescheduling) {
    return [];
  }

  const wantsActions = /\b(send|solve|snooze)\b/i.test(inputValue);
  if (inputValue.trim() && !wantsActions) {
    return [];
  }

  const all = sendOptions
    .filter((option) => option.section === ConversationDraftSendOptionSection.SendAnd)
    .map(({ id, label, description }) => ({ id, label, description: description ?? '' }));

  const snoozeDefinition = inputValue.trim() ? parseSnoozeCommand(inputValue) : null;
  const snoozeCommand = snoozeDefinition
    ? buildSnoozeCommand(snoozeDefinition, displayTime)
    : null;
  const dynamic =
    snoozeCommand && !all.some((command) => command.id === snoozeCommand.id)
      ? [snoozeCommand]
      : [];

  return wantsActions
    ? [...dynamic, ...all]
    : [...dynamic, ...all.filter((command) => DEFAULT_SEND_AND_IDS.has(command.id))];
};
